import { canonicalIdBase } from '@core-mkt/services/schema/schema-id.util';
import type { SchemaOrgNode } from '@core-mkt/services/schema/schema-org-node.model';
import type { CareerCenterArticleCard } from './career-center';

const ITEM_LIST_ID_FRAGMENT = 'articles-career-center';
const MAX_CONTENT_LENGTH = 10_000;
const URI_SCHEME_PREFIX = /^[a-z][a-z\d+.-]*:/i;

export type HtmlEntityDecoder = (value: string) => string;

/**
 * Builds the ItemList represented by a career-center resource grid.
 *
 * The card's first authored line is used as the ListItem name. This deliberately
 * treats block endings, line breaks, and literal newlines as boundaries instead
 * of using the first sentence: CMS titles do not consistently end in punctuation.
 */
export function getCareerCenterSchema(
  cards: CareerCenterArticleCard[] | null | undefined,
  pageUrl: string,
  decodeEntities: HtmlEntityDecoder,
): SchemaOrgNode | null {
  if (!Array.isArray(cards) || cards.length === 0) return null;

  const items = cards.map((card) => ({
    url: normalizeResourceUrl(card.relatedResourceLink, pageUrl),
    name: extractFirstAuthoredLine(card.relatedResourceContent, decodeEntities),
  }));

  // Preserve the one-ListItem-per-card contract. A partial list would silently
  // misrepresent the CMS grid, so malformed card data fails the whole fragment.
  if (items.some((item) => !item.url || !item.name)) return null;

  const itemListElement = items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    url: item.url,
    name: item.name,
  }));

  return {
    '@type': 'ItemList',
    '@id': `${canonicalIdBase(pageUrl)}#${ITEM_LIST_ID_FRAGMENT}`,
    numberOfItems: itemListElement.length,
    itemListElement,
  };
}

/**
 * Preserve CMS-authored HTTP(S) and path-style URLs, while failing closed for
 * executable or non-web schemes that should never appear in JSON-LD.
 */
function normalizeResourceUrl(value: string | null | undefined, pageUrl: string): string {
  const url = value?.trim() ?? '';
  if (!url || url.startsWith('//')) return '';
  if (URI_SCHEME_PREFIX.test(url) && !/^https?:\/\//i.test(url)) return '';

  try {
    const parsed = /^https?:\/\//i.test(url)
      ? new URL(url)
      : new URL(url.startsWith('/') ? url : `/${url}`, `${new URL(pageUrl).origin}/`);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : '';
  } catch {
    return '';
  }
}

function extractFirstAuthoredLine(value: string | null | undefined, decodeEntities: (value: string) => string): string {
  if (!value) return '';

  const bounded = value.slice(0, MAX_CONTENT_LENGTH);
  const lines = bounded
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .replace(/<\/(?:address|article|blockquote|div|h[1-6]|li|p|section)>/gi, '\n')
    .split(/\r?\n/);

  for (const line of lines) {
    // Drop well-formed tags, then any orphan `<` that could begin malformed markup.
    // A leftover `>` is valid plain text (e.g. "Rates > 1%"), so it is preserved.
    const text = decodeEntities(line.replace(/<[^>]*>/g, ' ').replace(/</g, ''))
      .replace(/\s+/g, ' ')
      .trim();
    if (text) return text;
  }

  return '';
}
